import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EAlertType, requestAuthentication } from 'shared/actions/authentication';
import { openChat } from 'shared/actions/chat';
import { CardSectionNew } from 'shared/components/CardSectionNew';
import { IChatHintSchema } from 'shared/repositories/chats/entities/chat_hint/ChatHintSchema';
import { selectOffer, selectUser } from 'shared/selectors';
import { selectIsOfferRedesignExperimentActive } from 'shared/selectors/abExperiments';
import { offerChatSelector } from 'shared/selectors/offerChat';
import { selectIsChatSuggestionsEnabled } from 'shared/selectors/selectIsChatSuggestionsEnabled';
import { selectIsOfferPageCommercialRedesignSelector } from 'shared/selectors/selectIsOfferPageCommercialRedesign';
import { getIsCommercialByOffer } from 'shared/utils/getIsCommercialByOffer';

import { trackHintQuestionClick } from './tracking';
import { HintQuestion, HintQuestions } from '../../components/HintQuestions';

export const HintQuestionsContainer: React.VFC = () => {
  const dispatch = useDispatch();
  const { isChatsEnabled, hintQuestions } = useSelector(offerChatSelector);
  const offer = useSelector(selectOffer);
  const isCommercial = getIsCommercialByOffer(offer);
  const isChatSuggestionsEnabled = useSelector(selectIsChatSuggestionsEnabled);
  const isRedesign = useSelector(selectIsOfferRedesignExperimentActive);
  const isRedesignForCommercial = useSelector(selectIsOfferPageCommercialRedesignSelector);

  const user = useSelector(selectUser);

  const onClick = React.useCallback(
    (text: string) => {
      const { analyticsKey, messageText } = hintQuestions.find(
        ({ buttonText }) => buttonText === text,
      ) as IChatHintSchema;

      if (!user || !user.isAuthenticated) {
        dispatch(
          requestAuthentication({
            onSuccess: () => {
              trackHintQuestionClick(offer, analyticsKey);

              return openChat({ defaultMessage: messageText });
            },
            alert: EAlertType.Chats,
          }),
        );

        return;
      }

      dispatch(openChat({ defaultMessage: messageText }));
      trackHintQuestionClick(offer, analyticsKey);
    },
    [hintQuestions, offer, dispatch, user],
  );

  if (!isChatSuggestionsEnabled || !isChatsEnabled || user?.cianUserId === offer.userId || !hintQuestions.length) {
    return null;
  }

  const Wrapper = isCommercial && isRedesignForCommercial ? CardSectionNew : React.Fragment;

  return (
    <Wrapper>
      <HintQuestions redesigned={isRedesign} commercialRedesigned={isCommercial && isRedesignForCommercial}>
        {hintQuestions.map(({ buttonText }, index) => (
          <HintQuestion key={index} onClick={onClick} text={buttonText} />
        ))}
      </HintQuestions>
    </Wrapper>
  );
};
